/**
 * Directive-aware deep merge used by the web-component config layer.
 *
 * Objects are merged recursively. Scalars replace. Arrays, by default, replace
 * wholesale (source wins) — this preserves the long-standing behaviour where an
 * override array fully supersedes the base array.
 *
 * To *extend* a base array without restating it, an override may supply an
 * array directive object instead of a bare array:
 *
 *   fields: { "$patch": [ { key: "companyName", label: "Organization" } ] }
 *     → merge-by-key onto the matching base entries, preserving base order.
 *       Unmatched patch entries are appended.
 *
 *   facets: { "$append": [ { key: "accountEnabled", ... } ] }
 *     → concatenate onto the base array, de-duplicating by key (an appended
 *       entry whose key already exists is skipped).
 *
 * Both directives may be combined in one object; `$patch` is applied first,
 * then `$append`. A bare array override (no directive) still replaces.
 *
 * The identity key for matching/de-duping is "key" (the shared identifier used
 * by fields and facets).
 */

/** Identity key used to match array entries for $patch/$append. */
const IDENTITY_KEY = 'key';

/** Directive wrapper allowing an override to extend a base array. */
export interface ArrayDirective<T = any> {
    /** Concatenate onto the base array, de-duplicating by identity key. */
    $append?: T[];
    /** Merge-by-key onto base entries (base order preserved); unmatched appended. */
    $patch?: T[];
}

/**
 * A config value that is an array in the base but may be supplied as either a
 * replacement array or an {@link ArrayDirective} in an override.
 */
export type ArrayOrDirective<T = any> = T[] | ArrayDirective<T>;

function isPlainObject(value: any): boolean {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function isArrayDirective(value: any): value is ArrayDirective {
    return (
        isPlainObject(value) &&
        (Array.isArray(value.$append) || Array.isArray(value.$patch)) &&
        // Only $append/$patch keys are permitted for a directive object.
        Object.keys(value).every((k) => k === '$append' || k === '$patch')
    );
}

function identityOf(entry: any): any {
    return isPlainObject(entry) ? entry[IDENTITY_KEY] : entry;
}

/**
 * Applies an {@link ArrayDirective} against a base array, returning a new array.
 * $patch runs first (merge-by-key, base order preserved, unmatched appended),
 * then $append (concat with de-dupe by identity key).
 */
function applyArrayDirective(baseArray: any[], directive: ArrayDirective): any[] {
    let result = Array.isArray(baseArray) ? [...baseArray] : [];

    if (Array.isArray(directive.$patch)) {
        for (const patch of directive.$patch) {
            const id = identityOf(patch);
            const idx = result.findIndex((entry) => identityOf(entry) === id);
            if (idx >= 0) {
                const existing = result[idx];
                result[idx] =
                    isPlainObject(existing) && isPlainObject(patch)
                        ? deepMerge(existing, patch)
                        : patch;
            } else {
                result.push(patch);
            }
        }
    }

    if (Array.isArray(directive.$append)) {
        const seen = new Set(result.map((entry) => identityOf(entry)));
        for (const entry of directive.$append) {
            const id = identityOf(entry);
            if (id !== undefined && seen.has(id)) continue;
            seen.add(id);
            result.push(entry);
        }
    }

    return result;
}

/**
 * Recursively merges `source` onto `target`, returning a new object.
 * See the module docs for array/directive semantics.
 */
export function deepMerge(target: any, source: any): any {
    const result = { ...target };
    for (const key of Object.keys(source)) {
        const sourceVal = source[key];
        const targetVal = target[key];

        if (isArrayDirective(sourceVal)) {
            result[key] = applyArrayDirective(Array.isArray(targetVal) ? targetVal : [], sourceVal);
        } else if (isPlainObject(sourceVal) && isPlainObject(targetVal)) {
            result[key] = deepMerge(targetVal, sourceVal);
        } else {
            result[key] = sourceVal;
        }
    }
    return result;
}
